/* eslint-disable react/jsx-key */
/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Module, Node, Waiting } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import MsDyn365 from '@msdyn365-commerce/core';
import { IAddressAddItem, IAddressAddUpdateProps } from '../../common/components/address-add';
import { IAddressList, IAddressListItem, IAddressListProps } from '../../common/components/address-list';
import { IAddressShowItem, IAddressShowProps } from '../../common/components/address-show';
import { IAccountManagementAddressViewProps } from './krannich-c-account-management-address';
import Modal from 'reactstrap/lib/Modal';
import { ModalHeader } from 'reactstrap';
import ModalBody from 'reactstrap/lib/ModalBody';
import { GridSize } from '../krannich-c-search-result-container/krannich-c-search-result-container';
import ModalFooter from 'reactstrap/lib/ModalFooter';
import { IAddressDeleteActionsProps } from '../../common/components/address-delete';

// import { ElectronicAddressType } from '@msdyn365-commerce/retail-proxy';

/**
 * Address show component.
 * @param param0 - Root param.
 * @param param0.AddressDetail - Address detail.
 * @param param0.items - IAddressShowItem[].
 * @returns - Address Node.
 */
interface IAddressShowPropsNew {
    editButton: React.ReactNode;
    removeButton: React.ReactNode;
    primaryButton: React.ReactNode;
    isShowPrimaryButton: boolean;
}
interface IAddressListPropsExtended extends IAddressListProps {
    resources?: any;
}
interface IAddressListExtended extends IAddressList {
    resources?: any;
}
const AddressShow: React.FC<IAddressShowProps & IAddressShowPropsNew> = ({
    AddressDetail,
    items,
    editButton,
    removeButton,
    isShowPrimaryButton,
    primaryButton,
    custPhoneNum
}) => {
    //VSI customization start for handling the different Cases
    //state item is not coming i.e Germany
    if (items.length === 6 && items.every(item => item.key !== 'State')) {
        const zipCodeIndex = items.findIndex(item => item.key === 'ZipCode');
        if (zipCodeIndex !== -1) {
            // Insert the new object before the 'ZipCode' object
            items.splice(zipCodeIndex, 0, { key: 'State', description: '-' });
        }
        // Now placing the City at the correct index
        const cityIndex = items.findIndex(item => item.key === 'City');
        if (cityIndex !== -1) {
            // Remove the object from its current position
            const cityObject = items.splice(cityIndex, 1)[0];
            // Insert the object at the 2nd index
            items.splice(2, 0, cityObject);
        }
    }
    //Statement below refers to the Case of India where structure needs to be set accordingly
    if (items.length === 11) {
        const stateIndex: number = items.findIndex(item => item.key === 'State');
        const zipCodeIndex: number = items.findIndex(item => item.key === 'ZipCode');
        if (stateIndex === 8) {
            const temp = items[Number(zipCodeIndex)];
            items[Number(zipCodeIndex)] = items[Number(stateIndex)];
            items[Number(stateIndex)] = temp;
        }
    }
    //VSI customization end

    return (
        <>
            {items.map((item: IAddressShowItem) => {
                if (item.key === 'Name') {
                    return <td key={item.key}>{item.description}</td>;
                } else if (item.key === 'Street') {
                    return <td key={item.key}>{item.description}</td>;
                } else if (item.key === 'City') {
                    return <td key={item.key}>{item.description}</td>;
                } else if (item.key === 'State') {
                    return <td key={item.key}>{item.description}</td>;
                } else if (item.key === 'ZipCode') {
                    return <td key={item.key}>{item.description}</td>;
                } else if (item.key === 'ThreeLetterISORegionName') {
                    return <td key={item.key}>{item.description}</td>;
                } else if (item.key === 'Phone') {
                    return <td key={item.key}>{isShowPrimaryButton || custPhoneNum === '' ? item.description : custPhoneNum}</td>;
                } else if (
                    item.key === 'County' ||
                    item.key === 'StreetNumber' ||
                    item.key === 'BuildingCompliment' ||
                    item.key === 'DistrictName' ||
                    item.key === 'Postbox'
                ) {
                    return null;
                } else {
                    return <td key={item.key}>-</td>;
                }
            })}
            <td>
                {editButton}
                {isShowPrimaryButton && removeButton}
            </td>
            <td className='current_primary'> {isShowPrimaryButton ? primaryButton : 'Current Primary Address'} </td>
        </>
    );
};

/**
 * Address List Component.
 * @param param0 - Root param.
 * @param param0.List - Address list.
 * @param param0.isShowList - Boolean.
 * @param param0.heading - Address heading.
 * @param param0.items - IAddressListItem[].
 * @returns - AddressListComponent Node.
 */
const AddressList: React.FC<IAddressListExtended> = ({
    List,
    isShowList,
    heading,
    items,
    toShowItemNumberProp,
    context,
    custPhoneNum,
    usSiteFlag,
    resources
}) => {
    // if (!isShowList) {
    //     return null;
    // }

    //*******VSI CUSTOMIZATION START*******//

    //Customization States and UseEffects
    const [addresess, setAddress] = React.useState<IAddressListItem[]>([]);
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [isDisabled, setIsDisabled] = React.useState<boolean>(false);
    const [counter, setCounter] = React.useState<number>(1);
    const [quan, setQuan] = React.useState<number>(Number(toShowItemNumberProp) || 2);
    const [toShowItemNumber, setToShowItemNumber] = React.useState(quan);

    //First time call to set number of items set to display from config otherwise default 2 and call addAddress function to set
    //addresses
    React.useEffect(() => {
        setQuan(Number(toShowItemNumberProp) || 2);
        addAddresses();
    }, []);

    React.useEffect(() => {
        setQuan(Number(toShowItemNumberProp) || 2);
        addAddresses();
    }, [items]);

    //Call after every change in toShwoItemNumber
    React.useEffect(() => {
        addAddresses();
    }, [toShowItemNumber]);

    //Disable viewmore button once TSX prop "items" arrary is equal to our custom addreses array
    React.useEffect(() => {
        items.length === addresess.length ? setIsDisabled(true) : setIsDisabled(false);
    }, [items, addresess]);

    //Update to display item number
    React.useEffect(() => {
        setToShowItemNumber(quan * counter);
    }, [counter]);

    //Function is used to set counter on every click on view more button
    const handleToShowItemNumer = () => {
        setCounter(counter + 1);
    };

    //Funciton is used to set the items in our custom array according to view more button clicks and default config dispaly number
    const addAddresses = () => {
        setIsLoading(true);
        const arr: IAddressListItem[] = [];
        for (let index = 0; index < toShowItemNumber; ++index) {
            // eslint-disable-next-line security/detect-object-injection
            items[index] && arr.push(items[index]);
        }
        setAddress(arr);
        setIsLoading(false);
    };
    //*******VSI CUSTOMIZATION END*******//

    const [isMobile, setIsMobile] = React.useState<boolean>();
    const _updateViewport = (): void => {
        const viewport = _getViewport();
        viewport === 'xs' || viewport === 'sm' ? setIsMobile(true) : setIsMobile(false);
    };

    const _getViewport = (): GridSize => {
        // Always render in mobile viewport on a mobile device
        if (context?.request && context.request.device && context.request.device.Type === 'Mobile') {
            return 'xs';
        }
        if (MsDyn365.isBrowser && window.innerWidth) {
            const gridSettings = context?.request.gridSettings;
            if (gridSettings) {
                if (gridSettings.xs && window.innerWidth <= gridSettings.xs.w) {
                    return 'xs';
                } else if (gridSettings.sm && window.innerWidth <= gridSettings.sm.w) {
                    return 'sm';
                } else if (gridSettings.md && window.innerWidth <= gridSettings.md.w) {
                    return 'md';
                } else if (gridSettings.lg && window.innerWidth <= gridSettings.lg.w) {
                    return 'lg';
                }
                return 'xl';
            }
        }

        return 'lg';
    };
    React.useEffect(() => {
        _updateViewport();
        if (MsDyn365.isBrowser && window.addEventListener) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            window.addEventListener('resize', _updateViewport);
        }
    }, []);
    if (isMobile) {
        return (
            <Node {...List} className='address-container'>
                {addresess.map((item: IAddressListItem, key: number) => {
                    const { error, isShowPrimaryButton, primaryButton, editButton, removeButton } = item;
                    const { Error, isShowError, title, message } = error;

                    return (
                        <Node className='address-inside-container'>
                            {item.showItems.items.map((item: IAddressShowItem) => {
                                if (item.key === 'ThreeLetterISORegionName') {
                                    return (
                                        <span className={`${item.key}-container`} key={item.key}>
                                            {'Country:'} <span>{item.description}</span>
                                        </span>
                                    );
                                } else if (item.key === 'Phone') {
                                    return (
                                        <span className={`${item.key}-container`} key={item.key}>
                                            {`${item.key}:`} <span>{isShowPrimaryButton ? item.description : custPhoneNum}</span>
                                        </span>
                                    );
                                } else {
                                    return (
                                        <span className={`${item.key}-container`} key={item.key}>
                                            {`${item.key}:`} <span>{item.description}</span>
                                        </span>
                                    );
                                }
                            })}
                            {isShowError && (
                                <Node {...Error} className='error-mobile-address-container'>
                                    {title}
                                    {message}
                                </Node>
                            )}
                            <td className='edit-delete-buttons'>
                                {editButton}
                                {isShowPrimaryButton && removeButton}
                            </td>
                            <td className='mob_current_primary'>
                                {' '}
                                {isShowPrimaryButton ? primaryButton : resources.currentPrimaryAddressLabel}{' '}
                            </td>
                        </Node>
                    );
                })}
                <button
                    className={`ms-mobile-order-history__btn-more  msc-btn ${isDisabled ? 'ctx-disabled-view-more' : ''}`}
                    onClick={() => {
                        handleToShowItemNumer();
                    }}
                    disabled={isDisabled}
                >
                    {resources.viewMoreLabel}
                </button>
            </Node>
        );
    } else {
        return (
            <Node {...List}>
                {isLoading ? (
                    <Waiting />
                ) : (
                    <>
                        <div className='account-address-table-wrapper'>
                            <table className='msc-address-list-table'>
                                <tr>
                                    <th>{resources.nameLabel}</th>
                                    <th>{resources.streetLabel}</th>
                                    <th>{resources.cityLabel}</th>
                                    <th>{resources.stateLabel}</th>
                                    {usSiteFlag ? <th>{resources.zipCodeLabel}</th> : <th>{resources.postCodeLabel}</th>}
                                    <th>{resources.countryLabel}</th>
                                    <th>{resources.phoneLabel}</th>
                                    <th>{resources.actionsLabel}</th>
                                    <th>{resources.addressSelectionLabel}</th>
                                </tr>
                                {addresess.map((item: IAddressListItem, key: number) => {
                                    const { error, showItems, isShowPrimaryButton, primaryButton, editButton, removeButton } = item;
                                    const { Error, isShowError, title, message } = error;

                                    return (
                                        <tr key={key}>
                                            <AddressShow
                                                {...showItems}
                                                editButton={editButton}
                                                removeButton={removeButton}
                                                isShowPrimaryButton={isShowPrimaryButton}
                                                primaryButton={primaryButton}
                                                custPhoneNum={custPhoneNum}
                                            />
                                            {isShowError && (
                                                <Node {...Error}>
                                                    {title}
                                                    {message}
                                                </Node>
                                            )}
                                        </tr>
                                    );
                                })}
                            </table>
                        </div>
                        <button
                            className={`ms-order-history__btn-more  msc-btn ${isDisabled ? 'ctx-disabled-view-more' : ''}`}
                            onClick={() => {
                                handleToShowItemNumer();
                            }}
                            disabled={isDisabled}
                        >
                            {resources.viewMoreLabel}
                        </button>
                    </>
                )}
            </Node>
        );
    }
};

/**
 * Address Lists Component.
 * @param param0 - Root param.
 * @param param0.ListAddress - List address.
 * @param param0.heading - List heading.
 * @param param0.isShowEmptyListMessage - Boolean.
 * @param param0.emptyListMessage - Empty list message.
 * @param param0.addButton - Add button.
 * @param param0.primaryAddressList - Primary address.
 * @param param0.otherAddressList - Other address.
 * @returns Address Lists Component Node.
 */
const AddressLists: React.FC<IAddressListPropsExtended> = ({
    ListAddress,
    heading,
    isShowEmptyListMessage,
    emptyListMessage,
    addButton,
    primaryAddressList,
    otherAddressList,
    toShowItemNumberProp,
    context,
    custPhoneNum,
    usSiteFlag,
    resources
}) => {
    const mergedAddressListItems: IAddressListItem[] = [...primaryAddressList.items, ...otherAddressList.items];
    const mergedAddress: IAddressList = {
        items: mergedAddressListItems,
        List: { className: 'msc-address-list__primary' },
        isShowList: false,
        heading: otherAddressList.heading ?? primaryAddressList.heading
    };
    return (
        <Node {...ListAddress}>
            <div className='address-heading-btn-wrapper'>
                {heading}
                {addButton}
            </div>
            {isShowEmptyListMessage && emptyListMessage}
            <AddressList
                {...mergedAddress}
                toShowItemNumberProp={toShowItemNumberProp}
                context={context}
                custPhoneNum={custPhoneNum}
                usSiteFlag={usSiteFlag}
                resources={resources}
            />
            {/* <AddressList {...primaryAddressList} />
                <AddressList {...otherAddressList} /> */}
        </Node>
    );
};

/**
 * AddressAddUpdateComponent.
 * @param param0 - Root param.
 * @param param0.AddressForm - Address form.
 * @param param0.heading - Address heading.
 * @param param0.items - IAddressAddItem[].
 * @param param0.hasError - Boolean.
 * @param param0.error - Error message.
 * @param param0.isShowSaveButton - Boolean.
 * @param param0.saveButton - Save button.
 * @param param0.isShowCancelButton - Boolean.
 * @param param0.cancelButton - Cancel button.
 * @returns Address add update component node.
 */
interface IAddressAddUpdatePropsExtended {
    formLabel?: string;
    usSiteFlag?: boolean;
    resources?: any;
}
const AddressAddUpdate: React.FC<IAddressAddUpdateProps & IAddressAddUpdatePropsExtended> = ({
    AddressForm,
    heading,
    items,
    hasError,
    error,
    isShowSaveButton,
    saveButton,
    isShowCancelButton,
    cancelButton,
    formLabel,
    usSiteFlag,
    resources
}) => {
    const getheading = () => {
        if (formLabel === 'Add') {
            return 'ADD ADDRESS';
        } else if (formLabel === 'Update') {
            return 'EDIT ADDRESS';
        } else {
            return null;
        }
    };
    return (
        <Modal
            wrapClassName='ms-add-address-mobile-view-modal add-address-popup-container'
            autoFocus
            fade={false}
            applicationNode='renderPage'
            isOpen={true}
        >
            <ModalHeader className='add-address-heading'>{getheading()}</ModalHeader>
            <ModalBody>
                <p className='info-text-address-popup'>{resources.requiredFieldsLabel}</p>
                <Node {...AddressForm}>
                    {heading}
                    {items.map((item: IAddressAddItem) => {
                        const { AddressItem, key, label, alert, input, customAlert, customLabel } = item;

                        if (
                            item.key === 'County' ||
                            item.key === 'StreetNumber' ||
                            item.key === 'BuildingCompliment' ||
                            item.key === 'DistrictName' ||
                            item.key === 'Postbox'
                        ) {
                            return null;
                        }
                        return (
                            <Node {...AddressItem} key={key}>
                                {customLabel !== null && usSiteFlag ? customLabel : label}
                                {alert}
                                {customAlert !== null ? customAlert : null}
                                {input}
                            </Node>
                        );
                    })}
                    {hasError && (
                        <Node {...error.AddressError}>
                            {error.title}
                            {error.message}
                        </Node>
                    )}
                    <div className='button-wrapper'>
                        {isShowSaveButton && saveButton}
                        {isShowCancelButton && cancelButton}
                    </div>
                </Node>
            </ModalBody>
        </Modal>
    );
};
interface IAddressDeleteActionsPropsExtended {
    heading?: string;
    body?: string;
}
const ConfirmDelete: React.FC<IAddressDeleteActionsProps & IAddressDeleteActionsPropsExtended> = ({
    saveButton,
    cancelButton,
    heading,
    body
}) => {
    return (
        <Modal
            wrapClassName='ms-add-address-mobile-view-modal add-address-popup-container delete-popup'
            autoFocus
            fade={false}
            applicationNode='renderPage'
            isOpen={true}
        >
            <ModalHeader className='delete-address-heading'>{heading}</ModalHeader>
            <ModalBody>
                <p className='info-text-address-popup'>{body}</p>
            </ModalBody>
            <ModalFooter>
                {saveButton}
                {cancelButton}
            </ModalFooter>
        </Modal>
    );
};
/**
 * Account Management Address View Component.
 * @param props - Props.
 * @returns - AccountAddressManagement Module.
 */
const AccountManagementAddressView: React.FC<IAccountManagementAddressViewProps> = props => {
    const {
        AccountAddressManagement,
        infoMessageBar,
        viewState,
        showAddressList,
        showAddOrUpdateAddress,
        screenReaderNotification,
        toShowItemNumber,
        currentOperation,
        custPhoneNum,
        isRemoveAddressButton,
        showDeleteAddress,
        resources,
        context,
        usSiteFlag
    } = props;
    return (
        <Module {...AccountAddressManagement}>
            {infoMessageBar}
            {/* //VSI CUSTOMIZATION to show address list  */}
            {true && (
                <AddressLists
                    {...showAddressList}
                    toShowItemNumberProp={toShowItemNumber}
                    context={context}
                    custPhoneNum={custPhoneNum}
                    usSiteFlag={usSiteFlag}
                    resources={resources}
                />
            )}
            {viewState.isShowAddOrUpdateAddress && (
                <AddressAddUpdate {...showAddOrUpdateAddress} formLabel={currentOperation} usSiteFlag={usSiteFlag} resources={resources} />
            )}
            {isRemoveAddressButton && (
                <ConfirmDelete {...showDeleteAddress} heading={resources?.deleteAddressHeading} body={resources?.deleteAddressBody} />
            )}
            {screenReaderNotification}
        </Module>
    );
};

export default AccountManagementAddressView;
